// validationConfig.js
// Centralized validation configuration for all survey steps
// Custom validation functions for complex validations
export const customValidators = {
  // Email validation
  email: (value) => {
    if (!value) return true; // Allow empty if not required
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? true : 'يرجى إدخال بريد إلكتروني صحيح';
  },

  // Phone validation (Saudi format)
  phone: (value) => {
    if (!value) return true; // Allow empty if not required
    const phoneRegex = /^(05|5)[0-9]{8}$/;
    return phoneRegex.test(value.replace(/\s/g, '')) ? true : 'يرجى إدخال رقم جوال صحيح (05xxxxxxxx)';
  },

  // Minimum length validation
  minLength: (min) => (value) => {
    if (!value) return true; // Allow empty if not required
    return value.length >= min ? true : `يجب أن يكون النص ${min} أحرف على الأقل`;
  },

  // Number range validation
  numberRange: (min, max) => (value) => {
    if (!value) return true; // Allow empty if not required
    const num = parseFloat(value);
    if (isNaN(num)) return 'يرجى إدخال رقم صحيح';
    if (num < min || num > max) return `يجب أن يكون الرقم بين ${min} و ${max}`;
    return true;
  },

  // Array not empty (for checkboxes/multi-select)
  arrayNotEmpty: (value) => {
    return Array.isArray(value) && value.length > 0 ? true : 'يرجى اختيار خيار واحد على الأقل';
  },

  // Array or corresponding "other" field validation
  arrayOrOtherField: (otherFieldName) => (value, allData) => {
    const hasArraySelection = Array.isArray(value) && value.length > 0;
    const hasOtherFieldValue = allData[otherFieldName] && allData[otherFieldName].trim() !== '';
    
    if (hasArraySelection || hasOtherFieldValue) {
      return true;
    }
    
    return 'يرجى اختيار خيار واحد على الأقل أو كتابة خيار آخر';
  }
};

export const validationRules = {
  // Step 0: SurveyIntro - typically no validation needed
  0: {
    required: [],
    conditionalRequired: {}
  },

  // Step 1: GeneralInfo (CompanyGeneralInfo)
  1: {
    required: ['activityField', 'companySize', 'companyAge', 'respondentPosition'],
    conditionalRequired: {
      'otherActivityType': (data) => data.activityType === 'أخرى',
      'otherPosition': (data) => data.respondentPosition === 'أخر'
    }
  },

  // Step 2: ImportanceAndBenefits
  2: {
    required: [
      'dataImportance',        // Required: importance level selection
      'operationalBenefits',   // Required: at least one operational benefit OR other field
      'legalBenefits',        // Required: at least one legal benefit OR other field
      'financialBenefits',    // Required: at least one financial benefit OR other field
      'securityBenefits',     // Required: at least one security benefit OR other field
      'dataImpact',           // Required: impact level selection
      'cooperationLevel',     // Required: cooperation level selection
      'nonDisclosureRisks'    // Required: at least one risk selection OR other field
    ],
    conditionalRequired: {},
    // Custom validations for array fields that allow "other" field as alternative
    customValidations: {
      'operationalBenefits': customValidators.arrayOrOtherField('otherOperationalBenefits'),
      'legalBenefits': customValidators.arrayOrOtherField('otherLegalBenefits'),
      'financialBenefits': customValidators.arrayOrOtherField('otherFinancialBenefits'),
      'securityBenefits': customValidators.arrayOrOtherField('otherSecurityBenefits'),
      'nonDisclosureRisks': customValidators.arrayOrOtherField('otherRisks')
    }
  },

  // Step 3: LeadershipPositions
  3: {
    required: [
      'leadershipFinancialData',    // Required: at least one financial data selection OR other field
      'leadershipHealthData',       // Required: at least one health data selection OR other field
      'leadershipLegalData',        // Required: at least one legal/security data selection OR other field
      'leadershipProfessionalData'  // Required: at least one professional data selection OR other field
    ],
    conditionalRequired: {},
    customValidations: {
      'leadershipFinancialData': customValidators.arrayOrOtherField('otherLeadershipFinancialData'),
      'leadershipHealthData': customValidators.arrayOrOtherField('otherLeadershipHealthData'),
      'leadershipLegalData': customValidators.arrayOrOtherField('otherLeadershipLegalData'),
      'leadershipProfessionalData': customValidators.arrayOrOtherField('otherLeadershipProfessionalData')
    }
  },

  // Step 4: SpecializedJobs
  4: {
    required: [
      'specializedFinancialData',    // Required: at least one financial data selection OR other field
      'specializedHealthData',       // Required: at least one health data selection OR other field
      'specializedLegalData',        // Required: at least one legal/security data selection OR other field
      'specializedProfessionalData', // Required: at least one professional data selection OR other field
      'specializedAcademicData'      // Required: at least one academic data selection OR other field
    ],
    conditionalRequired: {},
    customValidations: {
      'specializedFinancialData': customValidators.arrayOrOtherField('otherSpecializedFinancialData'),
      'specializedHealthData': customValidators.arrayOrOtherField('otherSpecializedHealthData'),
      'specializedLegalData': customValidators.arrayOrOtherField('otherSpecializedLegalData'),
      'specializedProfessionalData': customValidators.arrayOrOtherField('otherSpecializedProfessionalData'),
      'specializedAcademicData': customValidators.arrayOrOtherField('otherSpecializedAcademicData')
    }
  },

  // Step 5: AdministrativeJobs
  5: {
    required: [
      'adminFinancialData',      // Required: at least one financial data selection OR other field
      'adminHealthData',         // Required: at least one health data selection OR other field
      'adminLegalData',          // Required: at least one legal/security data selection OR other field
      'adminAcademicData'        // Required: at least one academic data selection OR other field
    ],
    conditionalRequired: {},
    customValidations: {
      'adminFinancialData': customValidators.arrayOrOtherField('otherAdminFinancialData'),
      'adminHealthData': customValidators.arrayOrOtherField('otherAdminHealthData'),
      'adminLegalData': customValidators.arrayOrOtherField('otherAdminLegalData'),
      'adminAcademicData': customValidators.arrayOrOtherField('otherAdminAcademicData')
    }
  },

  // Step 6: SkilledWorkers
  6: {
    required: [
      'skilledWorkersData'       // Required: at least one skilled workers data selection OR other field
    ],
    conditionalRequired: {},
    customValidations: {
      'skilledWorkersData': customValidators.arrayOrOtherField('otherSkilledWorkersData')
    }
  },

  // Step 7: CostsProcedures
  7: {
    required: [
      'financialCosts',            // Required: at least one financial cost selection OR other field
      'negativeEffects',           // Required: at least one negative effect selection OR other field
      'annualCosts',               // Required: annual costs estimate selection
      'verificationCostPerCandidate', // Required: verification cost per candidate selection
      'currentProcedures',         // Required: at least one current procedure selection OR other field
      'verificationTime',          // Required: verification time selection
      'monthlyApplicants',         // Required: monthly applicants selection
      'monthlyInterviews',         // Required: monthly interviews selection
      'disclosureChallenges'       // Required: at least one disclosure challenge selection OR other field
    ],
    conditionalRequired: {
      'costEstimationReason': (data) => data.annualCosts === 'يصعب تقديرها بدقة'
    },
    customValidations: {
      'financialCosts': customValidators.arrayOrOtherField('otherFinancialCosts'),
      'negativeEffects': customValidators.arrayOrOtherField('otherNegativeEffects'),
      'currentProcedures': customValidators.arrayOrOtherField('otherCurrentProcedures'),
      'disclosureChallenges': customValidators.arrayOrOtherField('otherDisclosureChallenges')
    }
  },

  // Step 8: StakeholderVerification
  8: {
    required: [
      'shareholdersVerification',    // Required: yes/no selection
      'suppliersVerification',       // Required: yes/no selection
      'otherParties',               // Required: at least one other party selection OR other field
      'verificationReasons'         // Required: at least one verification reason selection OR other field
    ],
    conditionalRequired: {
      'shareholdersSituations': (data) => data.shareholdersVerification === 'نعم',
      'suppliersSituations': (data) => data.suppliersVerification === 'نعم'
    },
    customValidations: {
      'otherParties': customValidators.arrayOrOtherField('otherPartiesCustom'),
      'verificationReasons': customValidators.arrayOrOtherField('otherVerificationReasons'),
      // Conditional array validations for when user selects "نعم"
      'shareholdersSituations': (value, allData) => {
        if (allData.shareholdersVerification === 'نعم') {
          return customValidators.arrayOrOtherField('otherShareholdersSituations')(value, allData);
        }
        return true; // Not required if they answered "لا"
      },
      'suppliersSituations': (value, allData) => {
        if (allData.suppliersVerification === 'نعم') {
          return customValidators.arrayOrOtherField('otherSuppliersSituations')(value, allData);
        }
        return true; // Not required if they answered "لا"
      }
    }
  },

  // Step 9: FinalComments - typically optional
  9: {
    required: [],
    conditionalRequired: {}
  }
};

// Helper function to validate a single field
export const validateField = (fieldName, value, rules, allData) => {
  // Check if field is required
  if (rules.required && rules.required.includes(fieldName)) {
    if (!value || value.toString().trim() === '') {
      // Don't show required error if there's a custom validation that might pass
      if (rules.customValidations && rules.customValidations[fieldName]) {
        // Let custom validation handle it
      } else {
        return 'هذا الحقل مطلوب';
      }
    }
  }

  // Check conditional required fields
  if (rules.conditionalRequired && rules.conditionalRequired[fieldName]) {
    const condition = rules.conditionalRequired[fieldName];
    if (condition(allData) && (!value || value.toString().trim() === '')) {
      return 'هذا الحقل مطلوب';
    }
  }

  // Check custom validations if defined
  if (rules.customValidations && rules.customValidations[fieldName]) {
    const validator = rules.customValidations[fieldName];
    const result = validator(value, allData); // Pass allData for validators that need it
    if (result !== true) {
      return result; // Return error message
    }
  }

  return null; // No error
};

// Helper function to validate entire step
export const validateStep = (stepIndex, formData) => {
  const rules = validationRules[stepIndex];
  if (!rules) return { isValid: true, errors: {} };

  const errors = {};

  // Validate required fields
  if (rules.required) {
    rules.required.forEach(field => {
      const error = validateField(field, formData[field], rules, formData);
      if (error) {
        errors[field] = error;
      }
    });
  }

  // Validate conditional required fields
  if (rules.conditionalRequired) {
    Object.keys(rules.conditionalRequired).forEach(field => {
      const error = validateField(field, formData[field], rules, formData);
      if (error) {
        errors[field] = error;
      }
    });
  }

  // Validate custom validations
  if (rules.customValidations) {
    Object.keys(rules.customValidations).forEach(field => {
      const error = validateField(field, formData[field], rules, formData);
      if (error) {
        errors[field] = error;
      }
    });
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Example of how to add custom validations to a specific step
export const addCustomValidationsExample = () => {
  // You can extend validation rules like this:
  validationRules[1].customValidations = {
    // Example: validate company name if provided
    companyName: customValidators.minLength(3),
    // Example: validate email field if you add one
    // email: customValidators.email,
    // Example: validate phone field if you add one
    // phone: customValidators.phone
  };
};

export default validationRules;